import { Quaternion, Vector3, Color } from "flavr-math";
import { RoomChangeType } from "../renderable/RendererModelInterface";
import { clamp, BusyIndicator } from "flavr-common";
import { FacetRenderer } from "../renderable/Renderable";
const tmpVector3_1 = new Vector3();
const tmpVector3_2 = new Vector3();
const tmpQuaternion = new Quaternion();
const tmpColor = new Color();
const ANIMATION_DURATION = 2.5;
const ANIMATION_VISIBLE_KEY_TIME = 0.7;
const ANIMATION_OFFSET = 0.3;
const NUM_BOXES = 4;
var AnimationState;
(function (AnimationState) {
    AnimationState[AnimationState["DISABLED"] = 0] = "DISABLED";
    AnimationState[AnimationState["STARTING"] = 1] = "STARTING";
    AnimationState[AnimationState["FADE_IN"] = 2] = "FADE_IN";
    AnimationState[AnimationState["ACTIVE"] = 3] = "ACTIVE";
    AnimationState[AnimationState["FADE_OUT"] = 4] = "FADE_OUT";
})(AnimationState || (AnimationState = {}));
export class BusyIndicatorRenderable extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.instances = [];
        this.scale = new Vector3();
        this.visible = true;
        this.roomVisible = true;
        this.progress = 0;
        this.animationTime = 0;
        this.state = AnimationState.STARTING;
        this.updateAnimation = (dt) => {
            var _a, _b;
            if (this.state === AnimationState.DISABLED) {
                return;
            }
            this.animationTime += dt;
            if (this.state === AnimationState.STARTING) {
                if (this.progress >= 1.0) {
                    for (const it of this.instances) {
                        it.setVisible(false);
                    }
                    this.state = AnimationState.DISABLED;
                    this.unregisterAnimationCallback();
                    if (((_a = this.facet) === null || _a === void 0 ? void 0 : _a.destroyOnFinished) === true) {
                        this.destroyMe();
                    }
                    return;
                }
                else if (this.animationTime >= ANIMATION_VISIBLE_KEY_TIME) {
                    if (this.isVisibleInScene()) {
                        for (const it of this.instances) {
                            it.setVisible(true);
                        }
                    }
                    this.state = AnimationState.FADE_IN;
                    this.animationTime = 0;
                }
                else {
                    return;
                }
            }
            else if (this.animationTime > ANIMATION_DURATION) {
                // End on animation state
                if (this.state === AnimationState.FADE_IN) {
                    this.state = this.progress < 1.0 ? AnimationState.ACTIVE : AnimationState.FADE_OUT;
                }
                else if (this.state === AnimationState.ACTIVE) {
                    if (this.progress >= 1.0) {
                        this.state = AnimationState.FADE_OUT;
                    }
                }
                else if (this.state === AnimationState.FADE_OUT) {
                    for (const it of this.instances) {
                        it.setVisible(false);
                    }
                    this.state = AnimationState.DISABLED;
                    if (((_b = this.facet) === null || _b === void 0 ? void 0 : _b.destroyOnFinished) === true) {
                        this.destroyMe();
                    }
                    else {
                        this.unregisterAnimationCallback();
                    }
                    return;
                }
                while (this.animationTime > ANIMATION_DURATION) {
                    this.animationTime -= ANIMATION_DURATION;
                }
            }
            const animationProgress = this.animationTime / ANIMATION_DURATION;
            const min = this.state === AnimationState.FADE_IN ? 0 : -1;
            const max = this.state === AnimationState.FADE_OUT ? 0 : 1;
            const normalizationFactor = 1 / (3 * (NUM_BOXES - 1));
            for (let x = 0; x < NUM_BOXES; ++x) {
                for (let y = 0; y < NUM_BOXES; ++y) {
                    for (let z = 0; z < NUM_BOXES; ++z) {
                        const instance = this.instances[NUM_BOXES * (NUM_BOXES * x + y) + z];
                        // the max value for (x + y + z) is 3*(NUM_BOXES-1)
                        // therfor we have to divide (x + y + z) by 3*(NUM_BOXES-1) and subtract 0.5
                        // to get values in the range [-0.5, 0.5]
                        const offset = ANIMATION_OFFSET * (normalizationFactor * (x + y + z) - 0.5);
                        const scale = tmpVector3_1.copy(this.scale);
                        const t = clamp(3 * (animationProgress - 0.5 - offset), min, max);
                        const s = 0.501 - 0.499 * Math.cos(t * Math.PI);
                        scale.multiplyScalar(s);
                        instance.setScale(scale);
                    }
                }
            }
        };
        const batch = this.owner.renderer.getBusyIndicatorBatch(this.owner.modelUID);
        if (batch) {
            for (let i = 0; i < NUM_BOXES * NUM_BOXES * NUM_BOXES; ++i) {
                if (i >= this.instances.length) {
                    const instance = batch.createInstance();
                    instance.setPosition(tmpVector3_1.set(0, 0, 0));
                    instance.setOrientation(tmpQuaternion.set(0, 0, 0, 1));
                    instance.setScale(tmpVector3_2.set(1, 1, 1));
                    instance.setColor(tmpColor.setHex(0x336699));
                    instance.setVisible(false);
                    this.instances.push(instance);
                }
            }
        }
        this.registerAnimationCallback(this.updateAnimation);
        this.subscribeRoomChange();
    }
    destroy() {
        this.unregisterAnimationCallback();
        for (const it of this.instances) {
            it.destroy();
        }
        this.instances = [];
    }
    setVisible(visible) {
        this.visible = visible;
        const showInstances = this.isVisibleInScene();
        for (const it of this.instances) {
            it.setVisible(showInstances);
        }
    }
    onRoomChanged(type) {
        this.roomVisible = type !== RoomChangeType.OUTSIDE_OF_CURRENT;
        this.setVisible(this.visible);
    }
    updateFacet(facet) {
        var _a;
        this.progress = (_a = facet.progress) !== null && _a !== void 0 ? _a : 0;
        if (this.state === AnimationState.DISABLED && this.progress < 1.0) {
            this.state = AnimationState.FADE_IN;
            if (this.isVisibleInScene()) {
                for (const it of this.instances) {
                    it.setVisible(true);
                }
            }
            this.registerAnimationCallback(this.updateAnimation);
        }
    }
    updateTransform() {
        const position = tmpVector3_1;
        const orientation = tmpQuaternion;
        const scale = this.scale;
        this.owner.getWorldTransform(position, orientation, scale);
        scale.multiplyScalar(0.99 / NUM_BOXES);
        const d = (NUM_BOXES - 1) / 2;
        for (let x = 0; x < NUM_BOXES; ++x) {
            for (let y = 0; y < NUM_BOXES; ++y) {
                for (let z = 0; z < NUM_BOXES; ++z) {
                    const instance = this.instances[NUM_BOXES * (NUM_BOXES * x + y) + z];
                    const offset = tmpVector3_2.set(scale.x * (x - d), scale.y * (y - d), scale.z * (z - d));
                    offset.applyQuaternion(orientation);
                    offset.add(position);
                    instance.setPosition(offset);
                    instance.setOrientation(orientation);
                    instance.setScale(scale);
                }
            }
        }
    }
    isVisibleInScene() {
        return this.visible && this.roomVisible && this.state !== AnimationState.DISABLED;
    }
}
BusyIndicatorRenderable.facetType = BusyIndicator;
